import { InAppState } from "../base/InAppState";
import template from "./SettingsTemplate.html";

import { _lcs } from "../../i18n";

/**
 * SettingsState — "Einstellungen" (menu drawer): the guest's own account.
 *
 * Two independent forms, each saved on its own:
 *
 *   • the name — PATCH /user/data. That endpoint REPLACES `user.data` rather
 *     than merging into it, so the request carries the account's other data
 *     fields along (see DATA_FIELDS).
 *   • the password — PATCH /auth/user/password, which checks the current one
 *     and answers 403 when it is wrong. Changing it leaves this session (and
 *     any other) signed in.
 *
 * "Als Gast fortfahren" has no account to edit, so that guest gets an offer to
 * create one instead of the forms.
 *
 * Above both: the switch for the house's push notifications (core/Push) —
 * for every guest, since a device subscribes with or without an account.
 */
export class SettingsState extends InAppState
{
    static ROUTE = "/settings";
    static ID = "settings";

    // The same minimum the signup asks for (the API itself accepts 6).
    static MIN_PASSWORD = 8;

    // The `user.data` keys PATCH /user/data accepts — anything else is a 400,
    // so the fields carried along are limited to these.
    static DATA_FIELDS = [ "title", "firstname", "lastname", "name", "email", "phone", "mobile", "company" ];

    showsBottomNav() { return false; }

    async enter()
    {
        this.isAnonymous = this.app.user?.guest === true;

        this.renderScreen( template, {
            isAnonymous : this.isAnonymous,
            name : this.app.session?.user?.data?.name || "",
            email : this.app.user?.email || "",
            minLength : SettingsState.MIN_PASSWORD
        } );

        const root = this.app.view.pageContent;
        this._on( "back", () => this.app.router.redirect( "/" ) );
        this.bindPush();

        if ( this.isAnonymous )
        {
            this._on( "signup", () => this.app.router.redirect( "/signup" ) );
            this._on( "login", () => this.app.router.redirect( "/login" ) );
            return;
        }

        this.nameForm = root.querySelector( '[data-id="name-form"]' );
        this.passwordForm = root.querySelector( '[data-id="password-form"]' );

        this.addEventListener( this.nameForm, "submit", ( e ) => this.onSaveName( e ) );
        this.addEventListener( this.passwordForm, "submit", ( e ) => this.onSavePassword( e ) );
        this.addEventListener( this.passwordForm, "click", ( e ) => this.onToggle( e ) );
    }

    async onSaveName( e )
    {
        e.preventDefault();
        const form = this.nameForm;
        const name = form.querySelector( "#settings-name" ).value.trim();

        this._showError( form, "" );
        if ( !name )
        {
            return this._showError( form, _lcs( "Bitte gib deinen Namen ein." ) );
        }

        const current = this.app.session?.user?.data || {};
        const data = {};
        SettingsState.DATA_FIELDS.forEach( ( key ) =>
        {
            if ( current[ key ] !== undefined ) data[ key ] = current[ key ];
        } );
        data.name = name;

        this._setBusy( form, true );
        try
        {
            const { result } = await this.app.api.patch( "/user/data", data );
            if ( !result || +result.status !== 200 || !result.data )
            {
                throw new Error( "Unexpected user data response." );
            }

            // The menu drawer and the home greeting read app.user, so both
            // show the new name from their next render on.
            if ( this.app.session )
            {
                this.app.session.user = result.data;
            }
            this.app.user = this.app.toLayoutUser( result.data );

            if ( this.isActive() )
            {
                form.querySelector( "#settings-name" ).value = result.data.data?.name || name;
            }
            this.app.view.toast( _lcs( "Dein Name wurde gespeichert." ), "success" );
        }
        catch ( err )
        {
            console.error( err );
            if ( this.isActive() )
            {
                this._showError( form, _lcs( "Dein Name konnte nicht gespeichert werden. Bitte versuche es später erneut." ) );
            }
        }
        finally
        {
            if ( this.isActive() )
            {
                this._setBusy( form, false );
            }
        }
    }

    async onSavePassword( e )
    {
        e.preventDefault();
        const form = this.passwordForm;
        const current = form.querySelector( "#pw-current" ).value;
        const next = form.querySelector( "#pw-new" ).value;
        const repeat = form.querySelector( "#pw-repeat" ).value;

        this._showError( form, "" );
        if ( !current )
        {
            return this._showError( form, _lcs( "Bitte gib dein aktuelles Passwort ein." ) );
        }
        if ( next.length < SettingsState.MIN_PASSWORD )
        {
            return this._showError( form, _lcs( "Das Passwort braucht mindestens 8 Zeichen." ) );
        }
        if ( next === current )
        {
            return this._showError( form, _lcs( "Das neue Passwort muss sich vom aktuellen unterscheiden." ) );
        }
        if ( next !== repeat )
        {
            return this._showError( form, _lcs( "Die Passwörter stimmen nicht überein." ) );
        }

        this._setBusy( form, true );
        try
        {
            const { result } = await this.app.api.patch( "/auth/user/password", {
                currentPassword : current,
                newPassword : next
            } );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected password response." );
            }

            if ( this.isActive() )
            {
                form.reset();
            }
            this.app.view.toast( _lcs( "Dein Passwort wurde geändert." ), "success" );
        }
        catch ( err )
        {
            console.error( err );
            if ( !this.isActive() )
            {
                return;
            }
            if ( +err?.status === 403 )
            {
                this._showError( form, _lcs( "Das aktuelle Passwort stimmt nicht." ) );
                form.querySelector( "#pw-current" ).focus();
            }
            else
            {
                this._showError( form, _lcs( "Das Passwort konnte nicht geändert werden. Bitte versuche es später erneut." ) );
            }
        }
        finally
        {
            if ( this.isActive() )
            {
                this._setBusy( form, false );
            }
        }
    }

    /* --------------------------- Push notifications --------------------------- */

    async bindPush()
    {
        const root = this.app.view.pageContent;
        this.pushToggle = root.querySelector( '[data-id="push-toggle"]' );
        this.addEventListener( this.pushToggle, "change", () => this.onTogglePush() );
        this._paintPush( await this.app.push.state() );
    }

    async onTogglePush()
    {
        const toggle = this.pushToggle;
        toggle.disabled = true;
        let state;
        try
        {
            // enable() asks for the browser's permission — this change event
            // is the tap it needs.
            state = toggle.checked ? await this.app.push.enable() : await this.app.push.disable();
            if ( toggle.checked && state === "on" )
            {
                this.app.view.toast( _lcs( "Benachrichtigungen sind aktiviert." ), "success" );
            }
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( _lcs( "Benachrichtigungen konnten nicht geändert werden." ), "error" );
            state = await this.app.push.state();
        }
        if ( this.isActive() )
        {
            this._paintPush( state );
        }
    }

    _paintPush( state )
    {
        const root = this.app.view.pageContent;
        const card = root.querySelector( '[data-id="push-card"]' );
        const row = root.querySelector( '[data-id="push-row"]' );
        const help = root.querySelector( '[data-id="push-help"]' );
        if ( !card )
        {
            return;
        }

        // Nothing to offer on this device: no card rather than a dead switch.
        if ( state === "unsupported" )
        {
            card.hidden = true;
            return;
        }

        card.hidden = false;
        row.hidden = state === "install" || state === "denied";
        this.pushToggle.checked = state === "on";
        this.pushToggle.disabled = false;

        const HELP = {
            "install" : _lcs( "Auf dem iPhone kommen Benachrichtigungen nur in der installierten App an. Füge die App zuerst zum Home-Bildschirm hinzu." ),
            "denied" : _lcs( "Benachrichtigungen sind für diese Seite blockiert. Du kannst sie in den Einstellungen deines Browsers wieder erlauben." ),
            "off" : _lcs( "Erhalte Hinweise zu deinem Aufenthalt, Veranstaltungen und Neuigkeiten aus dem Haus." ),
            "on" : _lcs( "Du erhältst Benachrichtigungen von diesem Haus auf diesem Gerät." )
        };
        help.textContent = HELP[ state ] || "";
    }

    /** Show / hide one password field (the eye button inside it). */
    onToggle( e )
    {
        const toggle = e.target.closest( "[data-toggle]" );
        if ( !toggle )
        {
            return;
        }
        const input = this.passwordForm.querySelector( "#" + toggle.getAttribute( "data-toggle" ) );
        const show = input.type === "password";
        input.type = show ? "text" : "password";
        toggle.setAttribute( "aria-pressed", String( show ) );
        toggle.setAttribute( "aria-label", show ? _lcs( "Passwort verbergen" ) : _lcs( "Passwort anzeigen" ) );
        const icon = toggle.querySelector( ".icon" );
        icon.classList.toggle( "icon--eye", !show );
        icon.classList.toggle( "icon--eye-off", show );
        input.focus();
    }

    _showError( form, message )
    {
        const el = form.querySelector( '[data-id="form-error"]' );
        el.textContent = message;
        el.hidden = !message;
    }

    _setBusy( form, busy )
    {
        const button = form.querySelector( 'button[type="submit"]' );
        button.disabled = busy;
        button.setAttribute( "aria-busy", String( busy ) );
    }

    _on( id, handler )
    {
        const el = this.app.view.pageContent.querySelector( '[data-id="' + id + '"]' );
        if ( el ) this.addEventListener( el, "click", handler );
    }
}
